import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getMediaUrl } from '@onesource/services';
import { FC, ReactNode } from 'react';
import { MdEmail, MdFacebook, MdLanguage, MdLaptopMac, MdLocationOn, MdPhone, MdTranslate } from 'react-icons/md';
import { SiInstagram, SiLinkedin, SiTiktok, SiX, SiYoutube } from 'react-icons/si';

import { normalizePhone } from './steps';

export type TProfilePreviewFullData = {
  fullName?: string | null;
  title?: string | null;
  bio?: string | null;
  casualImage?: string | null;
  landscapeImage?: string | null;
  professionNames: string[];
  offerings: string[];
  expertise: string[];
  languagesTaught: string[];
  teachingFormat?: string | null;
  certificates?: string | null;
  companyName?: string | null;
  companyEmail?: string | null;
  professionalWebsite?: string | null;
  phone?: string | null;
  businessStreet?: string | null;
  businessCity?: string | null;
  businessState?: string | null;
  businessCountry?: string | null;
  socials?: {
    facebook?: string | null;
    instagram?: string | null;
    youtube?: string | null;
    twitter?: string | null;
    linkedin?: string | null;
    tiktok?: string | null;
  } | null;
};

const FORMAT_LABELS: Record<string, string> = {
  in_person: 'In person',
  online: 'Online',
  both: 'Both',
};

const SOCIAL_ICONS: Array<{ key: keyof NonNullable<TProfilePreviewFullData['socials']>; icon: ReactNode }> = [
  { key: 'facebook', icon: <MdFacebook size={18} color="#1877F2" /> },
  { key: 'instagram', icon: <SiInstagram size={18} color="#E1306C" /> },
  { key: 'youtube', icon: <SiYoutube size={18} color="#FF0000" /> },
  { key: 'twitter', icon: <SiX size={18} /> },
  { key: 'linkedin', icon: <SiLinkedin size={18} color="#0A66C2" /> },
  { key: 'tiktok', icon: <SiTiktok size={18} /> },
];

const ChipSection: FC<{ label: string; items: string[] }> = ({ label, items }) => {
  if (items.length === 0) {
    return null;
  }
  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        {label}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {items.map(item => (
          <Chip key={item} label={item} size="small" variant="outlined" color="primary" />
        ))}
      </Stack>
    </Box>
  );
};

const SidebarRow: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <Box sx={{ display: 'flex', color: 'primary.main', mt: '2px', flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Stack>
);

type Props = {
  data: TProfilePreviewFullData;
};

export const ProDirectoryProfilePreviewFull: FC<Props> = ({ data }) => {
  const theme = useTheme();

  const phoneDisplay = normalizePhone(data.phone);
  const displayName = data.fullName || 'Your Name';
  const location = [data.businessCity, data.businessState, data.businessCountry].filter(Boolean).join(', ');
  const formatLabel = data.teachingFormat ? FORMAT_LABELS[data.teachingFormat] : undefined;
  const socialLinks = SOCIAL_ICONS.filter(({ key }) => !!data.socials?.[key]);
  const hasSidebarSecondary =
    !!location ||
    !!data.companyEmail ||
    !!phoneDisplay ||
    !!data.professionalWebsite ||
    !!data.businessStreet ||
    !!data.companyName ||
    !!formatLabel ||
    socialLinks.length > 0 ||
    data.languagesTaught.length > 0;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Hero band */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          padding: '40px 20px 40px',
          background: `
            linear-gradient(107deg, #fff 38%, rgba(255,255,255,0) 67%),
            radial-gradient(ellipse 560px 560px at 74% 32%, rgba(255,123,94,0.65) 0%, transparent 65%),
            radial-gradient(ellipse 560px 560px at 37% 104%, rgba(255,140,110,0.50) 0%, transparent 65%),
            radial-gradient(ellipse 480px 480px at 22% 153%, rgba(255,155,125,0.38) 0%, transparent 65%),
            radial-gradient(ellipse 480px 480px at 25% -20%, rgba(255,160,130,0.32) 0%, transparent 65%),
            #fff
          `,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 4 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Box sx={{ minWidth: 0, order: { xs: 2, md: 1 } }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ lineHeight: 1.1, wordBreak: 'break-word', letterSpacing: '-0.02em' }}
            >
              {displayName}
            </Typography>
            {data.title && (
              <Typography variant="subtitle1" color="text.primary" sx={{ mt: 1, maxWidth: 480 }}>
                {data.title}
              </Typography>
            )}
            {location && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 2 }}>
                <MdLocationOn size={16} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
                <Typography variant="body2" color="text.primary">
                  {location}
                </Typography>
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              order: { xs: 1, md: 2 },
              width: { xs: '100%', md: 560 },
              flexShrink: 0,
              aspectRatio: '4 / 3',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: 4,
            }}
          >
            {data.landscapeImage ? (
              <Box
                component="img"
                src={getMediaUrl(data.landscapeImage)}
                alt={displayName}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No photo added
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Body: bio + sidebar */}
      <Box sx={{ p: { xs: 3, md: 4 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="flex-start">
          {/* Left: About */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
              About {displayName}
            </Typography>
            <Typography
              variant="body2"
              color={data.bio ? 'text.primary' : 'text.secondary'}
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
            >
              {data.bio || 'No bio added yet.'}
            </Typography>

            {(data.expertise.length > 0 || data.offerings.length > 0 || data.certificates) && (
              <Stack spacing={3} sx={{ mt: 3 }}>
                <ChipSection label="Expertise" items={data.expertise} />
                <ChipSection label="Offerings & Services" items={data.offerings} />
                {data.certificates && <ChipSection label="Certificates" items={[data.certificates]} />}
              </Stack>
            )}
          </Box>

          {/* Right: sidebar card */}
          <Box
            sx={{
              width: { xs: '100%', md: 300 },
              flexShrink: 0,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: theme => theme.palette.primary.main + '0D',
              p: 2.5,
            }}
          >
            {(data.title || data.professionNames.length > 0) && (
              <Typography variant="body2" fontWeight={700} sx={{ mb: 2 }}>
                {data.title || data.professionNames.join(', ')}
              </Typography>
            )}

            {data.companyName && (
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                {data.companyName}
              </Typography>
            )}

            {hasSidebarSecondary ? (
              <Stack spacing={1.75} divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
                {location && (
                  <SidebarRow icon={<MdLocationOn size={18} />}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                      Location
                    </Typography>
                    <Typography variant="body2">{location}</Typography>
                    {data.businessStreet && (
                      <Typography variant="body2" color="text.secondary">
                        {data.businessStreet}
                      </Typography>
                    )}
                  </SidebarRow>
                )}

                {data.professionalWebsite && (
                  <SidebarRow icon={<MdLanguage size={18} />}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                      Website
                    </Typography>
                    <Link
                      href={
                        /^https?:\/\//i.test(data.professionalWebsite)
                          ? data.professionalWebsite
                          : `https://${data.professionalWebsite}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {data.professionalWebsite}
                    </Link>
                  </SidebarRow>
                )}

                {formatLabel && (
                  <SidebarRow icon={<MdLaptopMac size={18} />}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                      Sessions
                    </Typography>
                    <Typography variant="body2">{formatLabel}</Typography>
                  </SidebarRow>
                )}

                {data.companyEmail && (
                  <SidebarRow icon={<MdEmail size={18} />}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {data.companyEmail}
                    </Typography>
                  </SidebarRow>
                )}

                {phoneDisplay && (
                  <SidebarRow icon={<MdPhone size={18} />}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                      Phone
                    </Typography>
                    <Typography variant="body2">{phoneDisplay}</Typography>
                  </SidebarRow>
                )}

                {socialLinks.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontWeight: 600, mb: 1 }}
                    >
                      Social media
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {socialLinks.map(({ key, icon }) => (
                        <Link
                          key={key}
                          href={data.socials?.[key] ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          {icon}
                        </Link>
                      ))}
                    </Stack>
                  </Box>
                )}

                {data.languagesTaught.length > 0 && (
                  <SidebarRow icon={<MdTranslate size={18} />}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                      Languages
                    </Typography>
                    <Typography variant="body2">{data.languagesTaught.join(', ')}</Typography>
                  </SidebarRow>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No contact details added yet.
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
